import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Zap, BookOpen } from 'lucide-react';
import { olufiChat } from '../services/api';

// Logo served from public/ — no bundler import, works on Vercel
const LOGO_URL = process.env.PUBLIC_URL + '/gbongan_ai.png';

const WELCOME =
  'Welcome to Gbongan Archive! I am Gbongan AI \uD83C\uDF3F\n\n' +
  'I am your Historian, Reporter & Content Creator for all things Gbongan. ' +
  'Ask me about Gbongan\'s people, history, culture, locations, and news.\n\n' +
  ' RECENT UPDATES: We have decided to pause the service of Gbongan AI. The cost is currently too high for us to maintain it, especially the compute and storage of the RAG LLMs. We are exploring more sustainable ways to bring Gbongan AI back in the future. Thank you for your understanding ';

const QUICK_PROMPTS = [
  'Who is the king of Gbongan?',
  'Tell me about Gbongan history',
  'Who are the professors from Gbongan?',
  'What is the Gbongan oriki?',
  'Who are the medical doctors?',
  'Where is Gbongan?',
];

const THINKING_MESSAGES = [
  'Searching the archive\u2026',
  'Reading relevant records\u2026',
  'Crafting your answer\u2026',
];

// ── Inline markdown renderer ──────────────────────────────────────────────────
// Handles: **bold**, numbered lists (1) 2) etc), bullet lines (• - *), line breaks
// No external library needed — keeps the bundle small.
function renderMarkdown(text) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Empty line → spacing
    if (line.trim() === '') {
      elements.push(<span key={key++} className="block h-2" />);
      continue;
    }

    // Numbered list item: "1) ..." or "1. ..."
    const numberedMatch = line.match(/^(\d+)[).]\s+(.+)/);
    if (numberedMatch) {
      elements.push(
        <div key={key++} className="flex gap-2 mb-1">
          <span className="text-emerald-600 font-semibold flex-shrink-0 min-w-[1.2rem]">
            {numberedMatch[1]}.
          </span>
          <span>{renderInline(numberedMatch[2])}</span>
        </div>
      );
      continue;
    }

    // Bullet line: "• ..." or "- ..." or "* ..."
    const bulletMatch = line.match(/^[•\-*]\s+(.+)/);
    if (bulletMatch) {
      elements.push(
        <div key={key++} className="flex gap-2 mb-1">
          <span className="text-emerald-500 flex-shrink-0">•</span>
          <span>{renderInline(bulletMatch[1])}</span>
        </div>
      );
      continue;
    }

    // 💡 tip line
    if (line.startsWith('💡')) {
      elements.push(
        <div key={key++} className="mt-2 text-xs text-emerald-600 italic">
          {renderInline(line)}
        </div>
      );
      continue;
    }

    // Section header line (ends with colon, no bold markers)
    if (line.trim().endsWith(':') && !line.includes('**') && line.length < 60) {
      elements.push(
        <p key={key++} className="font-semibold text-gray-700 mt-1 mb-0.5">
          {line.trim()}
        </p>
      );
      continue;
    }

    // Regular paragraph
    elements.push(
      <p key={key++} className="mb-1">
        {renderInline(line)}
      </p>
    );
  }

  return elements;
}

// Render inline markdown: **bold**, *italic*, `code`
function renderInline(text) {
  if (!text) return null;

  // Split on **bold**, *italic*, `code` markers
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} className="bg-gray-100 text-emerald-700 px-1 rounded text-xs">{part.slice(1, -1)}</code>;
    }
    return part;
  });
}

// ── Source badge ──────────────────────────────────────────────────────────────
function SourceBadge({ source, chunksUsed }) {
  if (!source || source === 'welcome' || source === 'error') return null;

  let label = 'Gbongan AI';
  let icon  = null;

  if (source === 'gbongan_ai_rag') {
    label = `Archive · ${chunksUsed || 0} source${chunksUsed !== 1 ? 's' : ''}`;
    icon  = <Zap className="w-3 h-3 text-amber-500" />;
  } else if (source === 'gbongan_ai_retrieval') {
    label = `Archive search · ${chunksUsed || 0} record${chunksUsed !== 1 ? 's' : ''}`;
    icon  = <BookOpen className="w-3 h-3 text-emerald-500" />;
  } else if (source && source.includes('rag')) {
    label = `Archive · ${chunksUsed || 0} sources`;
    icon  = <Zap className="w-3 h-3 text-amber-500" />;
  }

  return (
    <div className="flex items-center gap-1 mt-1.5">
      {icon}
      <span className="text-xs text-gray-400">{label}</span>
    </div>
  );
}

// ── Message bubble ────────────────────────────────────────────────────────────
function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  return (
    <div
      className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
        isUser
          ? 'bg-emerald-700 text-white rounded-br-none'
          : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none shadow-sm'
      }`}
    >
      {isUser
        ? message.text
        : renderMarkdown(message.text)}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function GbonganAI() {
  const [open, setOpen]               = useState(false);
  const [messages, setMessages]       = useState([{ role: 'bot', text: WELCOME, source: 'welcome' }]);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [logoError, setLogoError]     = useState(false);
  const [thinkingMsg, setThinkingMsg] = useState(THINKING_MESSAGES[0]);
  const bottomRef                     = useRef(null);
  const inputRef                      = useRef(null);
  const thinkingTimer                 = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (loading) {
      let i = 0;
      thinkingTimer.current = setInterval(() => {
        i = (i + 1) % THINKING_MESSAGES.length;
        setThinkingMsg(THINKING_MESSAGES[i]);
      }, 4000);
    } else {
      clearInterval(thinkingTimer.current);
      setThinkingMsg(THINKING_MESSAGES[0]);
    }
    return () => clearInterval(thinkingTimer.current);
  }, [loading]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const res = await olufiChat(msg);
      const { reply, source, chunks_used } = res.data;
      setMessages((prev) => [...prev, {
        role: 'bot',
        text: reply,
        source,
        chunksUsed: chunks_used,
      }]);
    } catch {
      setMessages((prev) => [...prev, {
        role: 'bot',
        text: 'Sorry, We have decided to pause the service of Gbongan AI. The cost is currently too high for us to maintain it, especially the compute and storage of the RAG LLMs. We are exploring more sustainable ways to bring Gbongan AI back in the future. Thank you for your understanding ',
        source: 'error',
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const LogoImg = ({ className, alt }) =>
    logoError ? (
      <span className={className} role="img" aria-label="Gbongan AI"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem' }}>
        🌿
      </span>
    ) : (
      <img
        src={LOGO_URL}
        alt={alt || 'Gbongan AI'}
        className={className}
        onError={() => setLogoError(true)}
      />
    );

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-2xl overflow-hidden border-2 border-amber-400 hover:scale-110 transition-transform focus:outline-none bg-emerald-800"
        aria-label="Open Gbongan AI Chat"
        title="Gbongan AI – Historian, Reporter & Content Creator"
      >
        <LogoImg className="w-full h-full object-cover" alt="Gbongan AI" />
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse" />
        )}
      </button>

      {/* Chat Window */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-80 sm:w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col border border-amber-200 overflow-hidden"
          style={{ height: '580px' }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-emerald-800 to-emerald-700 text-white flex-shrink-0">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-amber-400 flex-shrink-0 bg-emerald-900 flex items-center justify-center">
              <LogoImg className="w-full h-full object-cover" alt="Gbongan AI" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm leading-tight">Gbongan AI</p>
              <p className="text-xs text-emerald-200 leading-tight">Historian, Reporter &amp; Content Creator</p>
            </div>
            <span className="flex items-center gap-1 text-xs text-green-300">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block" />
              Online
            </span>
            <button
              onClick={() => setOpen(false)}
              className="ml-1 text-white opacity-70 hover:opacity-100 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'bot' && (
                  <div className="w-7 h-7 rounded-full overflow-hidden mr-2 flex-shrink-0 self-end mb-1 bg-emerald-800 flex items-center justify-center text-xs">
                    <LogoImg className="w-full h-full object-cover" alt="" />
                  </div>
                )}
                <div className="flex flex-col max-w-[85%]">
                  <MessageBubble message={m} />
                  {m.role === 'bot' && (
                    <SourceBadge source={m.source} chunksUsed={m.chunksUsed} />
                  )}
                </div>
              </div>
            ))}

            {/* Thinking indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="w-7 h-7 rounded-full overflow-hidden mr-2 flex-shrink-0 bg-emerald-800 flex items-center justify-center">
                  <LogoImg className="w-full h-full object-cover" alt="" />
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-emerald-600 animate-spin flex-shrink-0" />
                  <span className="text-xs text-gray-500 animate-pulse">{thinkingMsg}</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick Prompts — first load only */}
          {messages.length === 1 && (
            <div className="px-3 pb-2 flex gap-1.5 flex-wrap bg-gray-50 border-t border-gray-100 pt-2">
              {QUICK_PROMPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => send(p)}
                  className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition"
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="flex items-center gap-2 px-3 py-3 border-t border-gray-200 bg-white flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask Gbongan AI about Gbongan…"
              className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-gray-50"
              disabled={loading}
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="p-2 bg-emerald-700 text-white rounded-xl hover:bg-emerald-800 transition disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>

          {/* Footer */}
          <div className="px-4 pb-2 text-center">
            <p className="text-xs text-gray-300">Gbongan AI · Historian, Reporter &amp; Content Creator</p>
          </div>
        </div>
      )}
    </>
  );
}
